import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { path } from 'ramda'
import { guid, ucFirst, hasHst, getDeviceLabel } from '../../common/ambient'
import classNames from 'classnames'
import Avatar from '../user/Avatar';
import {
  isValidPhone,
  isEmailVerified,
  isPhoneVerified,
  hasVerifiedContact as hasVerifiedContactHelper,
  createInviteAction,
  validateForm as validateFormHelper,
  getEmailInviteStatus,
  getPhoneInviteStatus
} from './AlertSetMemberCard.helpers'
import {
  formatPhoneInput,
  formatCodeInput,
  isPhoneAwaitingVerification,
  isPhoneFullyVerified
} from './phoneVerification.helpers'
import { alertSetOk } from '.'
import './GroupMemberFormModal.less'

function GroupMemberFormModal ({
  member,
  alertSets,
  devices,
  showAlertSets,
  showDevices,
  smsEnabled,
  userActionTypePrefix,
  onSave,
  user,
  userActions
}) {
  const isEditMode = !!(member?._id && !member?.isNew)
  const _alertSets = alertSets || []
  const _devices = devices || []
  const _showAlertSets = showAlertSets !== false
  const _showDevices = showDevices === true
  const _userActionTypePrefix = userActionTypePrefix || 'alert-set-group-member'

  // Track original contact info for detecting changes
  const [originalEmail] = useState(member?.email || '')
  const [originalPhone] = useState(member?.phone || '')

  const [formState, setFormState] = useState({
    name: member?.name || '',
    email: member?.email || '',
    phone: member?.phone || '',
    alertPrefs: member?.alertPrefs || [],
    alertSets: member?.alertSets || [],
    devicesEmail: member?.devicesEmail || [],
    devicesSms: member?.devicesSms || []
  })

  const [alertSetState, setAlertSetState] = useState({
    _id: '',
    alertPrefs: []
  })

  const [deviceState, setDeviceState] = useState({
    macAddress: '',
    alertPrefs: []
  })

  const [error, setError] = useState('')
  const [inviteMethods, setInviteMethods] = useState([])

  // Phone verification state for admin members
  const [phoneVerificationState, setPhoneVerificationState] = useState({
    phone: '',
    code: '',
    codeTries: 0,
    tooManyTries: false
  })

  // Track which fields are in edit mode (vs badge display mode)
  const [fieldEditMode, setFieldEditMode] = useState({
    email: true,
    phone: true
  })

  const [emailVerificationSent, setEmailVerificationSent] = useState(false)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormState({ ...formState, [name]: value })
    // Clear error when user starts typing
    if (error) setError('')
  }

  const handleCheckboxChange = (pref, prop = 'alertPrefs') => {
    setFormState(prev => {
      const newAlertPrefs = prev[prop].includes(pref)
        ? prev[prop].filter(p => p !== pref)
        : [...prev[prop], pref]

      return {
        ...prev,
        [prop]: newAlertPrefs
      }
    })
  }

  // Determine which invite methods to use based on filled fields and SMS availability
  const determineInviteMethods = (email, phone, smsEnabled) => {
    const methods = []
    if (email && email.trim()) methods.push('email')
    if (phone && phone.trim() && smsEnabled) methods.push('sms')
    return methods
  }

  // Update invite methods checkbox when form changes
  useEffect(() => {
    if (!isEditMode) {
      const methods = determineInviteMethods(formState.email, formState.phone, smsEnabled)
      setInviteMethods(methods)
    }
  }, [formState.email, formState.phone, smsEnabled, isEditMode])

  // Initialize field edit modes based on verification status
  useEffect(() => {
    if (isEditMode) {
      const emailStatus = getEmailInviteStatus(member || {}, user)
      const phoneStatus = getPhoneInviteStatus(member || {}, user)

      setFieldEditMode({
        email: !(emailStatus === 'verified' || emailStatus === 'pending'),
        phone: !(phoneStatus === 'verified' || phoneStatus === 'pending')
      })
    } else {
      // Create mode - always show inputs
      setFieldEditMode({ email: true, phone: true })
    }
  }, [member, isEditMode, user])

  // Validation
  const validate = () => {
    // Skip validation for admin members - their fields are read-only
    if (member?.admin) {
      return true
    }

    // Name is required
    if (!formState.name || !formState.name.trim()) {
      setError('Name is required')
      return false
    }

    // At least one of email or phone must be filled
    const hasEmail = formState.email && formState.email.trim()
    const hasPhone = formState.phone && formState.phone.trim()

    if (!hasEmail && !hasPhone) {
      setError('Please provide either an email address or phone number')
      return false
    }

    // If phone is filled, validate format
    if (hasPhone && !isValidPhone(formState.phone)) {
      setError('Phone number must have 10 digits')
      return false
    }

    return true
  }

  const handleSave = () => {
    if (!validate()) {
      return
    }

    const memberData = {
      _id: member?._id || guid(),
      name: formState.name,
      email: formState.email,
      phone: formState.phone,
      alertPrefs: formState.alertPrefs,
      alertSets: formState.alertSets,
      devicesEmail: formState.devicesEmail,
      devicesSms: formState.devicesSms,
      admin: member?.admin || false,  // Preserve admin flag from original member
      isNew: !isEditMode
    }

    // Detect contact changes for edit mode
    const emailChanged = isEditMode && formState.email !== originalEmail
    const phoneChanged = isEditMode && formState.phone !== originalPhone

    onSave({
      memberData,
      inviteMethods: isEditMode ? [] : inviteMethods, // Only send invites for new members
      emailChanged,
      phoneChanged
    })
  }

  const handleCancel = () => {
    userActions.doModal(null)
  }

  // Check if form is valid for enabling/disabling save button
  const isFormValid = () => {
    const hasName = formState.name && formState.name.trim()
    const hasEmail = formState.email && formState.email.trim()
    const hasPhone = formState.phone && formState.phone.trim()
    const hasContact = hasEmail || hasPhone
    const phoneValid = !hasPhone || isValidPhone(formState.phone)
    return hasName && hasContact && phoneValid
  }

  // Alert sets UI
  const renderAlertSets = () => {
    if (!_showAlertSets) return null

    // Only show alert options for verified contact methods
    const alertOptions = []

    if (isEditMode) {
      // Edit mode: only show verified methods
      if (isEmailVerified(member || {}, user)) {
        alertOptions.push('email')
      }
      if (isPhoneVerified(member || {}, user)) {
        alertOptions.push('sms')
      }
    } else {
      // New member: show all enabled methods
      alertOptions.push('email')
      if (smsEnabled) {
        alertOptions.push('sms')
      }
    }

    const remainingAlertSets = _alertSets
      .filter(_as => !formState.alertSets.find(mas => mas._id === _as._id))
      .filter(_as => alertSetOk(_as))

    const prefClasses = {
      email: 'glyphicon glyphicon-envelope',
      sms: 'glyphicon glyphicon-comment'
    }

    // Check if the member has either verified email or verified phone
    const hasEmailOrVerifiedPhone = isEditMode && member
      ? hasVerifiedContactHelper(member, user)
      : (formState.email?.trim() || formState.phone?.trim())

    // If no email/verified phone, don't render alert sets section at all
    if (!hasEmailOrVerifiedPhone) {
      return null
    }

    return (
      <div className="alert-sets-section">
        <label>Alert Sets</label>

        {remainingAlertSets.length > 0 && (
          <>
            <select
              className='form-control'
              value={alertSetState._id}
              onChange={e => {
                setAlertSetState({
                  _id: e.target.value,
                  alertPrefs: e.target.value ? alertOptions : []
                })
              }}>
              <option value="">Select Alert Set</option>
              {remainingAlertSets.map((set, i) => (
                <option key={i} value={set._id}>{set.title}</option>
              ))}
            </select>

            {alertSetState._id && (
              <div className="form-line alert-prefs">
                <div className="checkbox-group">
                  {alertOptions.map(pref => (
                    <label key={pref} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={alertSetState.alertPrefs && alertSetState.alertPrefs.includes(pref)}
                        onChange={() => {
                          setAlertSetState({
                            ...alertSetState,
                            alertPrefs: alertSetState.alertPrefs.includes(pref)
                              ? alertSetState.alertPrefs.filter(p => p !== pref)
                              : [...alertSetState.alertPrefs, pref]
                          })
                        }}
                      />
                      <i className={prefClasses[pref]} />
                      &nbsp;
                      {ucFirst(pref)}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {alertSetState._id && alertSetState.alertPrefs.length > 0 && (
              <a
                className="btn btn-primary add-alert-set"
                onClick={() => {
                  setFormState({
                    ...formState,
                    alertSets: [...formState.alertSets, alertSetState]
                  })
                  setAlertSetState({
                    _id: '',
                    alertPrefs: []
                  })
                }}
              >
                Add Alert Set
              </a>
            )}
          </>
        )}

        <div className="member-alert-sets">
          {formState.alertSets.map((as, i) => {
            const alertSet = _alertSets.find(_as => _as._id === as._id)
            const title = alertSet ? alertSet.title : 'alert set removed'
            return (
              <div className={classNames('member-alert-set alert-description', { inactive: !alertSet })} key={i}>
                <span>{title}</span>
                <div className='tools'>
                  {alertOptions.map(pref => {
                    const isActive = as.alertPrefs.includes(pref)
                    return (
                      <a
                        key={pref}
                        onClick={() => {
                          const newAlertSets = formState.alertSets.map(alertSet => {
                            if (alertSet._id === as._id) {
                              return {
                                ...alertSet,
                                alertPrefs: isActive
                                  ? alertSet.alertPrefs.filter(p => p !== pref)
                                  : [...alertSet.alertPrefs, pref]
                              }
                            }
                            return alertSet
                          })
                          setFormState({
                            ...formState,
                            alertSets: newAlertSets
                          })
                        }}
                      >
                        <i className={classNames(prefClasses[pref], { active: isActive })} />
                      </a>
                    )
                  })}
                  <a className="remove-alert-set" onClick={() => {
                    setFormState({
                      ...formState,
                      alertSets: formState.alertSets.filter(mas => mas._id !== as._id)
                    })
                  }}>
                    <span className="remove-icon" />
                  </a>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderDevices = () => {
    if (!_showDevices) return null

    const alertOptions = []

    if (isEditMode) {
      // Edit mode: only show verified methods
      if (isEmailVerified(member || {}, user)) {
        alertOptions.push('email')
      }
      if (isPhoneVerified(member || {}, user)) {
        alertOptions.push('sms')
      }
    } else {
      // New member: show all enabled methods
      alertOptions.push('email')
      if (smsEnabled) {
        alertOptions.push('sms')
      }
    }

    // Get unique assigned MACs
    const assignedMacs = [
      ...formState.devicesEmail,
      ...formState.devicesSms
    ]
    const uniqueAssignedMacs = [...new Set(assignedMacs)]

    // Filter out already assigned devices
    const remainingDevices = _devices.filter(dev =>
      !uniqueAssignedMacs.includes(dev.macAddress)
    )

    const prefClasses = {
      email: 'glyphicon glyphicon-envelope',
      sms: 'glyphicon glyphicon-comment'
    }

    const hasEmailOrVerifiedPhone = isEditMode && member
      ? hasVerifiedContactHelper(member, user)
      : (formState.email?.trim() || formState.phone?.trim())

    if (!hasEmailOrVerifiedPhone) {
      return null
    }

    return (
      <div className="alert-sets-section">
        <label>Heat Stress Devices</label>

        {remainingDevices.length > 0 && (
          <>
            <select
              className='form-control'
              value={deviceState.macAddress}
              onChange={e => {
                setDeviceState({
                  macAddress: e.target.value,
                  alertPrefs: e.target.value ? alertOptions : []
                })
              }}>
              <option value="">Select Device</option>
              {remainingDevices.map((dev, i) => (
                <option key={i} value={dev.macAddress}>
                  {getDeviceLabel(dev)}
                </option>
              ))}
            </select>

            {deviceState.macAddress && (
              <div className="form-line alert-prefs">
                <div className="checkbox-group">
                  {alertOptions.map(pref => (
                    <label key={pref} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={deviceState.alertPrefs && deviceState.alertPrefs.includes(pref)}
                        onChange={() => {
                          setDeviceState({
                            ...deviceState,
                            alertPrefs: deviceState.alertPrefs.includes(pref)
                              ? deviceState.alertPrefs.filter(p => p !== pref)
                              : [...deviceState.alertPrefs, pref]
                          })
                        }}
                      />
                      <i className={prefClasses[pref]} />
                      &nbsp;
                      {ucFirst(pref)}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {deviceState.macAddress && deviceState.alertPrefs.length > 0 && (
              <a
                className="btn btn-primary add-alert-set"
                onClick={() => {
                  const newDevicesEmail = deviceState.alertPrefs.includes('email')
                    ? [...formState.devicesEmail, deviceState.macAddress]
                    : formState.devicesEmail

                  const newDevicesSms = deviceState.alertPrefs.includes('sms')
                    ? [...formState.devicesSms, deviceState.macAddress]
                    : formState.devicesSms

                  setFormState({
                    ...formState,
                    devicesEmail: newDevicesEmail,
                    devicesSms: newDevicesSms
                  })
                  setDeviceState({
                    macAddress: '',
                    alertPrefs: []
                  })
                }}
              >
                Add Device
              </a>
            )}
          </>
        )}

        {/* Display assigned devices */}
        <div className="member-alert-sets">
          {uniqueAssignedMacs.map((mac, i) => {
            const device = _devices.find(d => d.macAddress === mac)
            const title = device ? getDeviceLabel(device) : 'device removed'

            return (
              <div className={classNames('member-alert-set alert-description', { inactive: !device })} key={i}>
                <span>{title}</span>
                <div className='tools'>
                  {alertOptions.map(pref => {
                    const deviceArray = pref === 'email' ? formState.devicesEmail : formState.devicesSms
                    const isActive = deviceArray.includes(mac)

                    return (
                      <a
                        key={pref}
                        onClick={() => {
                          const arrayName = pref === 'email' ? 'devicesEmail' : 'devicesSms'
                          const currentArray = formState[arrayName]

                          setFormState({
                            ...formState,
                            [arrayName]: isActive
                              ? currentArray.filter(m => m !== mac)
                              : [...currentArray, mac]
                          })
                        }}
                      >
                        <i className={classNames(prefClasses[pref], { active: isActive })} />
                      </a>
                    )
                  })}
                  <a className="remove-alert-set" onClick={() => {
                    setFormState({
                      ...formState,
                      devicesEmail: formState.devicesEmail.filter(m => m !== mac),
                      devicesSms: formState.devicesSms.filter(m => m !== mac)
                    })
                  }}>
                    <span className="remove-icon" />
                  </a>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // Render contact field with status badge and optional edit link
  const renderContactFieldWithBadge = (type, value, status, showEdit = true) => {
    const icons = {
      email: 'glyphicon glyphicon-envelope',
      phone: 'glyphicon glyphicon-comment'
    }

    const badgeText = status === 'verified' ? 'active' : 'pending'
    const badgeClass = status === 'verified' ? 'active' : 'pending'

    return (
      <div className="contact-field-display">
        <span className="contact-value">{value}</span>
        <div className={`contact-status-tag ${badgeClass}`}>
          <i className={icons[type]} />
          <span>{badgeText}</span>
        </div>
        {showEdit && (
          <button
            type="button"
            className="btn-link edit-link"
            onClick={() => setFieldEditMode({ ...fieldEditMode, [type]: true })}
          >
            edit
          </button>
        )}
      </div>
    )
  }

  // Render admin email verification UI
  const renderAdminEmailVerification = () => {
    const isVerified = path(['info', 'isVerified'], user)

    if (isVerified) {
      return renderContactFieldWithBadge('email', user.info.email, 'verified', false)
    }

    // Show pending badge with resend link
    const resendLinkText = (user.sendConfirmationSucceeded || emailVerificationSent)
      ? 'sent!'
      : 'resend'

    return (
      <div className="contact-field-display">
        <span className="contact-value">{user.info.email}</span>
        <div className="contact-status-tag pending">
          <i className="glyphicon glyphicon-envelope" />
          <span>pending</span>
        </div>
        <button
          type="button"
          className="btn-link edit-link"
          onClick={(e) => {
            e.stopPropagation()
            if (!emailVerificationSent && !user.sendConfirmationSucceeded) {
              userActions.sendConfirmation({ user: user.info._id, confirmKey: true })
              setEmailVerificationSent(true)
            }
          }}
          disabled={emailVerificationSent || user.sendConfirmationSucceeded}
        >
          {resendLinkText}
        </button>
      </div>
    )
  }

  // Render email field - either badge or input based on status and edit mode
  const renderEmailField = () => {
    if (member?.admin) {
      return renderAdminEmailVerification()
    }

    const emailStatus = getEmailInviteStatus(member || {}, user)

    // If verified or pending AND not in edit mode, show badge
    if ((emailStatus === 'verified' || emailStatus === 'pending') && !fieldEditMode.email) {
      const emailValue = formState.email
      return renderContactFieldWithBadge('email', emailValue, emailStatus, true)
    }

    // Otherwise show input field
    return (
      <input
        type='email'
        name='email'
        className='form-control'
        value={formState.email}
        onChange={handleInputChange}
        placeholder='Enter email address'
      />
    )
  }

  // Render phone field - either badge or input based on status and edit mode
  const renderPhoneField = () => {
    const phoneStatus = getPhoneInviteStatus(member || {}, user)

    // If verified or pending AND not in edit mode, show badge
    if ((phoneStatus === 'verified' || phoneStatus === 'pending') && !fieldEditMode.phone) {
      const phoneValue = member?.admin ? user.info.phone : formState.phone
      // Both admin and regular members can edit phone
      return renderContactFieldWithBadge('phone', phoneValue, phoneStatus, true)
    }

    // Otherwise show appropriate input/verification UI
    if (member?.admin) {
      return renderAdminPhoneVerification()
    }

    return (
      <>
        <input
          type='tel'
          name='phone'
          className='form-control'
          value={formState.phone}
          onChange={handleInputChange}
          placeholder='Enter phone number'
        />
        <div className='helper-text'>
          By pressing {isEditMode ? 'Save' : 'Invite'} I certify that I expressly have permission to send AWN alert text messages to the above number.
        </div>
      </>
    )
  }

  // Admin phone verification UI - phone entry step
  const renderAdminPhoneEntry = () => {
    return (
      <div className="admin-phone-verification">
        <input
          type='tel'
          className='form-control'
          value={phoneVerificationState.phone}
          onChange={(e) => {
            const formatted = formatPhoneInput(e.target.value)
            setPhoneVerificationState({
              ...phoneVerificationState,
              phone: formatted,
              tooManyTries: false
            })
          }}
          placeholder='Enter phone number'
        />
        <div className='helper-text'>
          Standard message rates apply.
        </div>
        {phoneVerificationState.tooManyTries && (
          <div className='helper-text warning'>
            Too many verification attempts. Please enter a new phone number.
          </div>
        )}
        <button
          className='btn btn-primary'
          disabled={phoneVerificationState.phone.length !== 12}
          onClick={() => {
            userActions.patch(user.info._id, { phone: phoneVerificationState.phone })
            setPhoneVerificationState({
              ...phoneVerificationState,
              tooManyTries: false
            })
          }}
        >
          Send Verification Code
        </button>
      </div>
    )
  }

  // Admin phone verification UI - code entry step
  const renderAdminPhoneCodeEntry = () => {
    return (
      <div className="admin-phone-verification">
        <div className='helper-text'>
          Please enter the 4-digit code sent to {user.info.phone}.{' '}
          <button
            type="button"
            className='btn-link'
            onClick={() => {
              if (window.confirm('Change phone number? This will clear the current verification code.')) {
                userActions.patch(user.info._id, { phone: '', phoneConfirmKey: false })
                setPhoneVerificationState({
                  phone: '',
                  code: '',
                  codeTries: 0,
                  tooManyTries: false
                })
              }
            }}
          >
            Change Number
          </button>
        </div>
        <input
          type='text'
          className='form-control'
          value={phoneVerificationState.code}
          onChange={(e) => {
            const formatted = formatCodeInput(e.target.value)
            setPhoneVerificationState({
              ...phoneVerificationState,
              code: formatted
            })
          }}
          placeholder='Enter 4-digit code'
        />
        {phoneVerificationState.codeTries > 0 && phoneVerificationState.codeTries < 3 && (
          <div className='helper-text warning'>
            Incorrect code. {3 - phoneVerificationState.codeTries} attempt{3 - phoneVerificationState.codeTries !== 1 ? 's' : ''} remaining.
          </div>
        )}
        <button
          className='btn btn-primary'
          disabled={phoneVerificationState.code.length !== 4}
          onClick={() => {
            if (phoneVerificationState.code === user.info.phoneConfirmKey) {
              // Verification success
              userActions.patch(user.info._id, { phoneConfirmKey: false })
              setPhoneVerificationState({
                phone: '',
                code: '',
                codeTries: 0,
                tooManyTries: false
              })
            } else {
              // Verification failed
              const newTries = phoneVerificationState.codeTries + 1
              if (newTries >= 3) {
                // Too many tries - reset phone
                userActions.patch(user.info._id, { phone: '', phoneConfirmKey: false })
                setPhoneVerificationState({
                  phone: '',
                  code: '',
                  codeTries: 0,
                  tooManyTries: true
                })
              } else {
                setPhoneVerificationState({
                  ...phoneVerificationState,
                  code: '',
                  codeTries: newTries
                })
              }
            }
          }}
        >
          Verify Code
        </button>
      </div>
    )
  }

  // Admin phone verification UI - main coordinator
  const renderAdminPhoneVerification = () => {
    // If phone is verified AND we're in edit mode, allow user to enter new phone
    if (isPhoneFullyVerified(user) && fieldEditMode.phone) {
      return renderAdminPhoneEntry()
    }

    // If phone is verified AND NOT in edit mode, show disabled (badge mode)
    if (isPhoneFullyVerified(user)) {
      return (
        <>
          <input
            type='tel'
            className='form-control'
            value={user.info.phone || ''}
            disabled
          />
          <div className='helper-text'>
            Phone verified
          </div>
        </>
      )
    } else if (isPhoneAwaitingVerification(user)) {
      // Phone is set but needs verification
      return renderAdminPhoneCodeEntry()
    } else {
      // No phone set - show entry form
      return renderAdminPhoneEntry()
    }
  }

  return (
    <div className='alert-group-member-form-modal'>

      <header>
        <Avatar name={!member?.admin && (formState?.name || '?')} />
        {member?.admin ? (
          <span className="member-name">Group Admin (You)</span>
        ) : (
          <div className='form-line'>
            <input
              type='text'
              name='name'
              className='form-control'
              value={formState.name}
              onChange={handleInputChange}
              placeholder='Enter name'
              disabled={member?.admin}
            />
          </div>
        )}
      </header>

      <div className='form'>
        {error && (
          <div className='error-message'>
            {error}
          </div>
        )}


        <div className='form-line'>
          {renderEmailField()}
        </div>

        {smsEnabled && (
          <div className='form-line'>
            {renderPhoneField()}
          </div>
        )}

        {_showAlertSets && renderAlertSets()}
        {_showDevices && renderDevices()}
      </div>

      <div className='modal-actions'>
        <a className='btn btn-secondary' onClick={handleCancel}>
          Cancel
        </a>
        <a
          className={`btn btn-primary ${!isFormValid() ? 'disabled' : ''}`}
          onClick={handleSave}
        >
          {isEditMode ? 'Save' : 'Invite'}
        </a>
      </div>
    </div>
  )
}

GroupMemberFormModal.propTypes = {
  member: PropTypes.object, // null for create mode
  alertSets: PropTypes.array,
  devices: PropTypes.array,
  showAlertSets: PropTypes.bool,
  showDevices: PropTypes.bool,
  smsEnabled: PropTypes.bool.isRequired,
  userActionTypePrefix: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  userActions: PropTypes.object.isRequired
}

GroupMemberFormModal.displayName = 'GroupMemberFormModal'

export default GroupMemberFormModal
